import {
  ANALYTICS_MODES,
  type AnalyticsMode,
  DEFAULT_ANALYTICS_MODE,
  INVENTORY_ITEM_STATUSES,
  type InventoryItemStatus,
  SHOPIFY_DEFAULT_PLATFORM_SHARE_BPS,
  type ShippingAddress,
  type StorefrontFeatureKey,
  type StorefrontTheme,
  type TenantModuleOverrides,
} from "@loot-os/api-contract";
import { sql } from "drizzle-orm";
import {
  type AnyPgColumn,
  bigint,
  boolean,
  char,
  check,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  real,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";

import { MANUAL_WEIGHTING, type Weighting } from "../../boxProcessing/weighting.js";
import { tenant } from "./auth.js";
// Referenced lazily (inside a `references` callback) by inventory_items; battles.ts imports this
// module's tables the same way, so neither side reads the other during evaluation.
import { battleOpens, battlePlayers } from "./battles.js";
import { catalogItems } from "./fulfillment/catalog-item.js";
import { vendorListings } from "./fulfillment/vendor-listing.js";

/**
 * Key-string legibility only (los_live_sk_… vs los_test_sk_…). Data partitioning
 * is done by environment_id; `mode` survives solely on api_keys, where it is
 * inherent to the immutable key string ("test" = any sandbox environment).
 */
export const MODES = ["live", "test"] as const;
export type Mode = (typeof MODES)[number];

export const ENVIRONMENT_TYPES = ["live", "sandbox"] as const;
export type EnvironmentType = (typeof ENVIRONMENT_TYPES)[number];

/**
 * Trust tier, boxed parity (`AccountRiskLevel`): an ordinal computed on read from stored facts
 * (`packages/core/src/fraud/trustTier.ts`), never a stored score. Only `end_users.trust_tier_override`
 * persists; `super_trusted` is reached through it or by a verified identity that never paid by card.
 */
export const TRUST_TIERS = [
  "untrusted",
  "trusted",
  "trusted_7d",
  "trusted_30d",
  "super_trusted",
] as const;
export type TrustTier = (typeof TRUST_TIERS)[number];

/**
 * Lock reason: why an account is locked, the fixed list the dashboard offers and every automated
 * locker sets (boxed parity: `LockAccountReason`, reduced — toxicity, the PayPal and Nuvei
 * disputes and the deleted-account markers have no Loot OS counterpart). `dispute` is a Stripe
 * dispute, `suspected_fraud` a SEON decline, the two `kyc_*` codes an identity-verification
 * decline or a duplicate identity. The free text beside it is the lock note, `end_users.lock_reason`.
 */
export const LOCK_REASONS = [
  "user_request",
  "manual",
  "kyc_declined",
  "kyc_duplicate",
  "suspected_fraud",
  "dispute",
] as const;
export type LockReason = (typeof LOCK_REASONS)[number];

/** The flows SEON screens; boxed's `account_login` is deliberately absent (disabled there too). */
export const FRAUD_ACTIONS = ["account_register", "purchase", "withdrawal"] as const;
export type FraudAction = (typeof FRAUD_ACTIONS)[number];

/** SEON's verdict, lowercased. `review` is also what an unreachable SEON yields (see `source`). */
export const FRAUD_STATES = ["approve", "review", "decline"] as const;
export type FraudState = (typeof FRAUD_STATES)[number];

/** `seon` when SEON answered; `unavailable` when it did not and `state` is `review` by policy. */
export const FRAUD_SOURCES = ["seon", "unavailable"] as const;
export type FraudSource = (typeof FRAUD_SOURCES)[number];

/**
 * Identity-verification (KYC) session status, boxed parity (`KycStatus`) lowercased: Veriff's
 * own `verification.status` / event `action` values. `approved` and `declined` are terminal;
 * `review` waits on Veriff; the rest let the end user start or resume a session.
 */
export const KYC_STATUSES = [
  "created",
  "started",
  "submitted",
  "approved",
  "declined",
  "resubmission_requested",
  "expired",
  "abandoned",
  "review",
] as const;
export type KycStatus = (typeof KYC_STATUSES)[number];

/** Why an approved verification still does not count as verified (boxed's `kycVerificationIssue`). */
export const KYC_POLICY_ISSUES = ["age", "country", "duplicate"] as const;
export type KycPolicyIssue = (typeof KYC_POLICY_ISSUES)[number];

export const KYC_PROVIDERS = ["veriff"] as const;
export type KycProviderName = (typeof KYC_PROVIDERS)[number];

/** One SEON rule that fired, as SEON reports it. */
export interface FraudAppliedRule {
  id: string;
  name: string;
  operation: string;
  score: number;
}

/** The small subset of SEON's response the dashboard shows; never the raw IP, email or session. */
export interface FraudSignals {
  ipCountry?: string;
  ipScore?: number;
  ipVpn?: boolean;
  ipTor?: boolean;
  ipProxy?: boolean;
  emailScore?: number;
  emailDisposable?: boolean;
  deviceHash?: string;
}

const timestamps = {
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
};

// ---- environments (Stripe-style: one live env per tenant + N sandboxes) ----------

/**
 * Writer: tenant admin (environments.create/rename/delete), except `kyc_required` and
 * `analytics_mode`, which only platform staff set (platform.tenants.setKycRequired /
 * setAnalyticsMode), and `fraud_screening_enabled`, which nothing writes after the insert. Whole
 * row is safe to return to tenant members.
 */
export const environments = pgTable(
  "environments",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    type: text("type", { enum: ENVIRONMENT_TYPES }).notNull(),
    name: text("name").notNull(),
    /**
     * DNS-label-safe identifier used in storefront hostnames
     * ({tenantSlug}--{envSlug}.ROOT_DOMAIN). Immutable across renames so the
     * hostname stays stable.
     */
    slug: text("slug").notNull(),
    /**
     * SEON screening of this environment's sign-ups, checkouts and withdrawals. On for every
     * environment (default true, backfilled in 0021) and switched from nowhere: whether screening
     * actually runs is decided by the stage's `SeonApiKey`. Trust-tier limits apply regardless.
     */
    fraudScreeningEnabled: boolean("fraud_screening_enabled").notNull().default(true),
    /**
     * Identity verification (KYC) required before an end user may withdraw: an unverified user is
     * `untrusted` and `POST /v1/fulfillment/orders` answers `kyc_required`. On by default; platform
     * staff only may switch it off (the platform holds the Veriff credential and pays per
     * verification).
     */
    kycRequired: boolean("kyc_required").notNull().default(true),
    /**
     * Whether this environment's storefront records browser analytics events, and under what
     * consent (`ANALYTICS_MODES`, docs/analytics.md): consent required by default. Platform staff
     * only set it; server-side events are recorded whatever it says.
     */
    analyticsMode: text("analytics_mode", { enum: ANALYTICS_MODES })
      .$type<AnalyticsMode>()
      .notNull()
      .default(DEFAULT_ANALYTICS_MODE),
    /**
     * Where Loot OS POSTs this environment's order events (`TenantOrderChanged`, signed with the
     * environment's derived secret); NULL = none are sent. Platform staff only set it
     * (`platform.tenants.setOrderEventsUrl`). Not a secret.
     */
    orderEventsUrl: text("order_events_url"),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Exactly one live environment per tenant.
    uniqueIndex("environments_tenant_live_idx").on(t.tenantId).where(sql`${t.type} = 'live'`),
    uniqueIndex("environments_tenant_name_idx").on(t.tenantId, t.name),
    uniqueIndex("environments_tenant_slug_idx").on(t.tenantId, t.slug),
    check("environments_slug_lower", sql`${t.slug} = lower(${t.slug})`),
  ],
);

// ---- tenant settings (1:1 with Better Auth tenant) --------------------

/**
 * Writers: tenant admin (tenant.updateSettings: the session TTL and the opens pause) and platform
 * staff (platform.tenants.create / setModules / setCommercialTerms: the modules and the shares).
 * Whole row is safe to return to tenant members.
 */
export const tenantSettings = pgTable("tenant_settings", {
  tenantId: text("tenant_id")
    .primaryKey()
    .references(() => tenant.id, { onDelete: "cascade" }),
  sessionTtlMinutes: integer("session_ttl_minutes").notNull().default(60),
  opensPaused: boolean("opens_paused").notNull().default(false),
  /**
   * The modules platform staff switched off (or back on), merged onto every-module-on; `{}` = the
   * tenant has everything. Read only through `parseTenantModules` / `resolveTenantModules`.
   */
  modules: jsonb("modules").$type<TenantModuleOverrides>().notNull().default({}),
  /** The platform's share of the tenant's storefront profit, basis points. Reporting only. */
  storefrontProfitShareBps: integer("storefront_profit_share_bps").notNull().default(0),
  /**
   * The platform's fee on the tenant's settled Shopify box sales, basis points: the rate the
   * Shopify app bills as usage charges (capped by the rate the merchant approved).
   */
  shopifyPlatformShareBps: integer("shopify_platform_share_bps")
    .notNull()
    .default(SHOPIFY_DEFAULT_PLATFORM_SHARE_BPS),
  ...timestamps,
});

// ---- api keys ----------------------------------------------------------------

/**
 * Writer: tenant admin (apiKeys.create/updateOrigins/revoke), and platform provisioning of the
 * internal storefront keys, through `createApiKey` / `revokeApiKey`. An insert, or the update
 * that sets `revoked_at`, writes its audit_logs row through the `api_keys_audit` trigger
 * (migration 0076); `api_keys_guard` refuses changes to type, key_hash, tenant_id,
 * environment_id, internal and a set revoked_at. key_hash must never reach any response.
 */
export const apiKeys = pgTable(
  "api_keys",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    type: text("type", { enum: ["secret", "publishable"] }).notNull(),
    /** Derived from the environment at creation: live env → "live", sandbox → "test". */
    mode: text("mode", { enum: MODES }).notNull(),
    /** Display prefix, e.g. "los_live_sk_3fj2" — never enough to reconstruct. */
    prefix: text("prefix").notNull(),
    /** sha256 hex of the full key. */
    keyHash: text("key_hash").notNull(),
    name: text("name").notNull().default(""),
    /** Platform-provisioned (hosted storefront) — hidden from tenant key lists, not tenant-revocable. */
    internal: boolean("internal").notNull().default(false),
    /** Publishable keys only: exact origins allowed to use this key from a browser. */
    allowedOrigins: jsonb("allowed_origins").$type<string[]>().notNull().default([]),
    lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("api_keys_key_hash_idx").on(t.keyHash),
    index("api_keys_tenant_idx").on(t.tenantId),
    index("api_keys_environment_idx").on(t.environmentId),
  ],
);

// ---- end users -----------------------------------------------------------------

/**
 * Writer: the tenant's own server through the public /v1 API; tenant editors through the dashboard
 * for the lock, trust-tier override, `notes` and `shipping_address` columns; the shopper through
 * `PUT /v1/me/shipping-address` for `shipping_address` and `PUT /v1/me/username` for `username`
 * (the one column other shoppers see); the Veriff webhooks for the `kyc_*` columns. Whole row is safe to return to that tenant's dashboard. `notes` is staff-facing and
 * must never reach `/v1/me`, the shopper's own view; `toEndUser` allowlists its columns, which is
 * what keeps `notes` out of every /v1 response. `shipping_address` reaches the shopper only through
 * its own route, `GET /v1/me/shipping-address`, never through `toEndUser`.
 */
export const endUsers = pgTable(
  "end_users",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** The tenant's own identifier for this user. */
    externalId: text("external_id").notNull(),
    /**
     * The user's public name, chosen by the shopper (`PUT /v1/me/username`; rules in
     * `@loot-os/api-contract` `usernameSchema`); null until they do. Unique per environment
     * regardless of case (`end_users_env_username_idx` on `lower(username)`). What other shoppers
     * see of a user: a battle seat's display name.
     */
    username: text("username"),
    /** Provably-fair per-user domain separator (boxed parity: 8 chars of a uuid). */
    salt: char("salt", { length: 8 }).notNull(),
    /** Monotonic provably-fair counter; multi-opens consume consecutive values. */
    nonce: integer("nonce").notNull().default(0),
    /** Integer US cents; every movement is a balance_ledger row (mutateBalance). */
    balanceCents: bigint("balance_cents", { mode: "number" }).notNull().default(0),
    /**
     * Integer US cents held by a reveal not yet claimed: the price of the user's unclaimed open
     * group (boxed's revealedItemsPurchasePrice). Spendable balance is balance_cents − held_cents:
     * what the storefront shows, what openBox and every battle entry guard on. Written only by
     * openBox, which adds the price at the reveal, and claimReveal, which releases it as it
     * debits. A battle seat holds nothing: its entry is debited when the seat is taken
     * (payBattleEntry).
     */
    heldCents: bigint("held_cents", { mode: "number" }).notNull().default(0),
    /** Reveal mode: when set, the server claims a reveal immediately after it commits. */
    autoClaim: boolean("auto_claim").notNull().default(false),
    /**
     * The notification categories the shopper turned off (`NotificationCategory[]`, optional
     * ones only), written by the shopper alone (`PUT /v1/me/notification-settings`). `[]` = every
     * category on. Read only through `parseNotificationOptOuts`.
     */
    notificationOptOuts: jsonb("notification_opt_outs").notNull().default([]),
    metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default({}),
    /** When set, the account is locked: opens, claims, and balance purchases are refused. */
    lockedAt: timestamp("locked_at", { withTimezone: true }),
    /** The lock reason (`LOCK_REASONS`). Null only on a lock that predates the list. */
    lockReasonCode: text("lock_reason_code", { enum: LOCK_REASONS }),
    /** The lock note: free text beside the code, a staff comment or the automated locker's detail. */
    lockReason: text("lock_reason"),
    /**
     * Provenance: `dashboard:<memberUserId>`, `stripe:dispute:<disputeId>`, `seon:<assessmentId>`
     * or `kyc:<verificationId>`.
     */
    lockedBy: text("locked_by"),
    /** Staff notes about this user, written from the dashboard; never shown to the shopper. */
    notes: text("notes"),
    /**
     * The end user's shipping address (`shippingAddressSchema`), saved by the shopper or recorded
     * by staff; an order copies its own at creation. Read through `parseStoredShippingAddress`.
     */
    shippingAddress: jsonb("shipping_address").$type<ShippingAddress>(),
    /** When set, replaces the computed trust tier (boxed parity: `riskLevelOverride`). */
    trustTierOverride: text("trust_tier_override", { enum: TRUST_TIERS }),
    /** Provenance of the override: `dashboard:<memberUserId>`. */
    trustTierOverrideBy: text("trust_tier_override_by"),
    trustTierOverrideAt: timestamp("trust_tier_override_at", { withTimezone: true }),
    /**
     * Latest identity-verification status, denormalized from `kyc_verifications` (boxed parity:
     * `Account.kycStatus`); null = never started.
     */
    kycStatus: text("kyc_status", { enum: KYC_STATUSES }),
    /**
     * Set when a verification was approved *and* passed the policy (age, document country, no
     * duplicate identity). The trust tier reads this, never the status.
     */
    kycVerifiedAt: timestamp("kyc_verified_at", { withTimezone: true }),
    /**
     * Withdrawal orders submitted (boxed's AccountData `physicalWithdrawalsSubmittedCount`): one per
     * order the withdrawal saga's account transaction commits (a CREATE; a join adds none). Boxed
     * counts when its order reaches PROCESSING, which here happens on the Fulfillment API after the
     * saga has answered, so the saga counts at creation instead. Written by the saga only.
     */
    physicalWithdrawalsSubmitted: integer("physical_withdrawals_submitted").notNull().default(0),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("end_users_env_external_idx").on(t.environmentId, t.externalId),
    uniqueIndex("end_users_env_username_idx").on(t.environmentId, sql`lower(${t.username})`),
    check("end_users_balance_non_negative", sql`${t.balanceCents} >= 0`),
    check("end_users_held_non_negative", sql`${t.heldCents} >= 0`),
  ],
);

// ---- boxes ---------------------------------------------------------------------

/**
 * Where an item comes from: the Loot OS catalog, a vendor marketplace listing, or a voucher the
 * house issued. A `VOUCHER` never reaches a drop table from an editor — `dropTableItemInput`
 * accepts a catalog item or a vendor listing and refuses every other source — but it is the
 * source an `inventory_items.item` snapshot carries after a cancelled marketplace order paid
 * the shopper back in kind.
 */
export enum DropTableItemSource {
  CATALOG = "CATALOG",
  MARKETPLACE = "MARKETPLACE",
  VOUCHER = "VOUCHER",
}

/**
 * What `drop_table_items.item_source` accepts. Wider than `DropTableItemSource` by `EXTERNAL`,
 * a copied boxed entry with no Loot OS row behind it, which no publish may author.
 */
export const DROP_TABLE_ITEM_ROW_SOURCES = [
  "CATALOG",
  "MARKETPLACE",
  "VOUCHER",
  "EXTERNAL",
] as const;
export type DropTableItemRowSource = (typeof DROP_TABLE_ITEM_ROW_SOURCES)[number];

/** What `drop_table_items.published_source` accepts: the two sources a publish can author. */
export const DROP_TABLE_ITEM_PUBLISHED_SOURCES = ["CATALOG", "MARKETPLACE"] as const;
export type DropTableItemPublishedSource = (typeof DROP_TABLE_ITEM_PUBLISHED_SOURCES)[number];

/**
 * What wrote a `box_versions` row: an editor publish, a reprocess run, the boxed mirror, or a
 * Shopify rule box's refresh (its drop table rebuilt from the merchant's rules).
 */
export const BOX_VERSION_TRIGGERS = ["editor", "reprocess", "sync", "rules"] as const;
export type BoxVersionTrigger = (typeof BOX_VERSION_TRIGGERS)[number];

export interface DropTableItem {
  /** catalog_items.id for versions published after the merge; legacy versions hold free-form ids. */
  itemId: string;
  itemSource: DropTableItemSource;
  name: string;
  imageUrl: string;
  /** Item value in integer US cents. */
  value: number;
  /** Integer weight; a published drop table's weights sum to exactly 1,000,000. */
  weight: number;
  /**
   * `drop_table_items.price_cents`: the vendor's asking price on a MARKETPLACE entry, absent on
   * every other source. Under `value` for a vendor whose shipping Loot OS adds on top, which is
   * why a withdrawal compares the live vendor price against this figure and never against `value`.
   */
  listingPriceCents?: number;
  /**
   * `drop_table_items.is_limited`: a counted number of this item can be won. Every MARKETPLACE
   * entry is limited; a CATALOG entry is limited when its availability strategy is FIXED.
   */
  isLimited?: boolean;
  /**
   * `drop_table_items.limited_quantity`: how many remain as of the last processor run, null on an
   * unlimited entry. The one entry field outside `dropTableHash`, because stock moves constantly
   * and must not write a version.
   */
  limitedQuantity?: number | null;
  /**
   * `drop_table_items.is_gem_substitute`: the item can no longer be shipped (no stock on a FIXED
   * item, an INACTIVE or sold-out listing, or a SUPER_UNFULFILLABLE or ARCHIVED item that is not
   * FIXED), so winning it pays its value back. A FIXED item's status never sets it.
   */
  isGemSubstitute?: boolean;
}

/** Which of the three boxed.gg box kinds a box is. Packs are a separate entity, never a box type. */
export enum BoxType {
  NORMAL = "NORMAL",
  VENDING_MACHINE = "VENDING_MACHINE",
  COMMUNITY = "COMMUNITY",
}

export const boxTypeEnum = pgEnum("box_type", [
  BoxType.NORMAL,
  BoxType.VENDING_MACHINE,
  BoxType.COMMUNITY,
]);

/**
 * An opener style: the animation a box opens with (`kind`, an `OPENER_KINDS` member in
 * api-contract) and the theme tokens it overrides on top of its host's theme. Writer: platform
 * staff for platform styles (`tenant_id` and `environment_id` NULL, `platform.openerStyles.*`),
 * a tenant editor for the environment's own (`openerStyles.*`). Whole row is safe to
 * return. Referenced by `boxes`, `storefront_configs`, `shopify_shops` and `platform_settings`,
 * each NULL = inherit; `assertOpenerStyleUsable` decides which styles each may name.
 */
export const boxOpenerStyles = pgTable(
  "box_opener_styles",
  {
    id: text("id").primaryKey(), // ulid
    /** NULL = a platform style, usable everywhere. */
    tenantId: text("tenant_id").references(() => tenant.id, { onDelete: "cascade" }),
    /** NULL iff tenant_id is NULL; an environment style is usable only in its environment. */
    environmentId: text("environment_id").references(() => environments.id, {
      onDelete: "cascade",
    }),
    name: text("name").notNull(),
    /**
     * The animation (`OpenerKind`). Text rather than an enum, so adding a kind needs no migration.
     * Read only through `parseOpenerKind`.
     */
    kind: text("kind").notNull(),
    /**
     * Theme overrides (`OpenerThemeOverrides`, api-contract); `{}` = every token follows the host.
     * Read only through `parseOpenerThemeOverrides`.
     */
    theme: jsonb("theme").notNull().default({}),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("box_opener_styles_env_idx").on(t.environmentId),
    check(
      "box_opener_styles_env_matches_tenant",
      sql`(${t.tenantId} IS NULL) = (${t.environmentId} IS NULL)`,
    ),
  ],
);

/** Writer: tenant editor for tenant rows, platform staff for box templates (tenant_id NULL). Whole row is safe to return. */
export const boxes = pgTable(
  "boxes",
  {
    id: text("id").primaryKey(), // ulid
    /** NULL = box template (authored by Loot OS, usable by any tenant that enables it). */
    tenantId: text("tenant_id").references(() => tenant.id, { onDelete: "cascade" }),
    /** NULL iff tenant_id is NULL (box templates are global; tenant boxes are env-scoped). */
    environmentId: text("environment_id").references(() => environments.id, {
      onDelete: "cascade",
    }),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    imageUrl: text("image_url").notNull().default(""),
    /**
     * The box art beyond the main image (boxed parity: background, overlay, and a mobile set of
     * all three). "" = none; an empty mobile slot falls back to its desktop one. Tenant-assets
     * CDN URLs when uploaded, any https URL when pasted.
     */
    backgroundImageUrl: text("background_image_url").notNull().default(""),
    overlayImageUrl: text("overlay_image_url").notNull().default(""),
    mobileImageUrl: text("mobile_image_url").notNull().default(""),
    mobileBackgroundImageUrl: text("mobile_background_image_url").notNull().default(""),
    mobileOverlayImageUrl: text("mobile_overlay_image_url").notNull().default(""),
    priceCents: integer("price_cents").notNull().default(0),
    status: text("status", { enum: ["draft", "active", "archived"] })
      .notNull()
      .default("draft"),
    /** Set only by the Boxed mirror; every box Loot OS authors is normal. */
    type: boxTypeEnum("type").notNull().default(BoxType.NORMAL),
    /**
     * Shown in the storefront home page's featured row. Merchandising only: it decides where a
     * box appears, never who may read it, so no query gates visibility on it.
     */
    featured: boolean("featured").notNull().default(false),
    /** Version currently used for opens; FK enforced in code (chicken-and-egg on insert). */
    currentVersion: integer("current_version").notNull().default(0),
    openedCount: bigint("opened_count", { mode: "number" }).notNull().default(0),
    /**
     * How the processor chooses weights (boxed parity: `weightingType` + `margin` + the two
     * dynamic entries). Copied onto each `box_versions` row at write so a version replays.
     */
    weighting: jsonb("weighting").$type<Weighting>().notNull().default(MANUAL_WEIGHTING),
    /**
     * When the drop table last changed in a way a shopper can see, so a client can compare the way
     * boxed clients do (boxed parity: `lastRefresh`). A stock-only update leaves it alone.
     */
    refreshedAt: timestamp("refreshed_at", { withTimezone: true }),
    /** Processor-stamped markers, boxed parity: `HAS_LIMITED_QTY`. */
    tags: text("tags").array().notNull().default([]),
    /** The mirrored boxed box's id, set only on a box Loot OS mirrors rather than authors. */
    externalId: text("external_id"),
    /**
     * The shopper who forged this box (a forge box, `type` COMMUNITY); null on every box a tenant
     * editor, platform staff or the boxed mirror wrote. Only ever set with `type` COMMUNITY
     * (`boxes_creator_is_community`). Public through the creator's display name only, never the id.
     */
    creatorEndUserId: text("creator_end_user_id").references(() => endUsers.id, {
      onDelete: "cascade",
    }),
    /**
     * The creator's cut of every purchase in force on the box, in basis points (0–300 = 0–3%):
     * already clamped to the environment's forge settings (`effectiveCreatorCutBps`), and
     * rewritten with `weighting.marginPercent` when those settings change. The processor copies it
     * onto each version (`box_versions.creator_cut_bps`), which is what commission reads. Null on
     * a box with no creator.
     */
    creatorCutBps: integer("creator_cut_bps"),
    /**
     * When the forge box's creator deleted it. The row stays, because opens pin its versions; a
     * deleted box is also `archived`, and no shopper list, read or open reaches it.
     */
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
    /**
     * The opener style this box opens with; NULL = inherit (the Shopify shop's, then the
     * environment's, then the platform default). A box template names platform styles only.
     */
    openerStyleId: text("opener_style_id").references((): AnyPgColumn => boxOpenerStyles.id),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("boxes_opener_style_idx").on(t.openerStyleId),
    uniqueIndex("boxes_env_slug_idx").on(t.environmentId, t.slug),
    index("boxes_env_creator_idx").on(t.environmentId, t.creatorEndUserId),
    // NULLS-DISTINCT makes the env index blind to box templates; give them their own.
    uniqueIndex("boxes_template_slug_idx").on(t.slug).where(sql`${t.tenantId} IS NULL`),
    uniqueIndex("boxes_tenant_external_idx")
      .on(t.tenantId, t.externalId)
      .where(sql`${t.externalId} IS NOT NULL`),
    index("boxes_tenant_idx").on(t.tenantId),
    index("boxes_active_idx").on(t.status).where(sql`${t.status} = 'active'`),
    check("boxes_env_matches_tenant", sql`(${t.tenantId} IS NULL) = (${t.environmentId} IS NULL)`),
    check(
      "boxes_weighting_type",
      sql`${t.weighting}->>'type' in ('MANUAL', 'WEIGHT_TARGET', 'PRICE_TARGET')`,
    ),
    // One-directional: a mirrored boxed COMMUNITY box has no creator, and that is allowed.
    check(
      "boxes_creator_is_community",
      sql`(${t.creatorEndUserId} IS NULL AND ${t.creatorCutBps} IS NULL) OR ${t.type} = 'COMMUNITY'`,
    ),
    check(
      "boxes_creator_cut_range",
      sql`${t.creatorCutBps} IS NULL OR ${t.creatorCutBps} BETWEEN 0 AND 300`,
    ),
  ],
);

/** Immutable snapshot of a box's drop table — the verifiability guarantee. */
export const boxVersions = pgTable(
  "box_versions",
  {
    id: text("id").primaryKey(), // ulid
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    version: integer("version").notNull(),
    /**
     * The entries as one document. Superseded by `drop_table_items`, which every reader moves to;
     * the writer keeps both until the contract migration drops this column.
     */
    dropTable: jsonb("drop_table").$type<DropTableItem[]>().notNull(),
    /** sha256 over the version's entries (`dropTableHash`); the generation the write rule compares. */
    dropTableHash: text("drop_table_hash").notNull().default(""),
    /** When this generation was written. A stock-only update writes no version, so this never moves. */
    refreshedAt: timestamp("refreshed_at", { withTimezone: true }).notNull().defaultNow(),
    /** What wrote the version: an editor publish, a reprocess run, the boxed mirror, or rules. */
    trigger: text("trigger", { enum: BOX_VERSION_TRIGGERS }).notNull().default("editor"),
    /** The box's weighting config at write, so a past version replays with what produced it. */
    weighting: jsonb("weighting").$type<Weighting>().notNull().default(MANUAL_WEIGHTING),
    /** Expected value in US cents at publish time. */
    ev: integer("ev").notNull(),
    priceCents: integer("price_cents").notNull(),
    /**
     * The forge creator's cut this version was priced with, in basis points: a copy of
     * `boxes.creator_cut_bps` at write, 0 on a box with no creator. Commission is computed from
     * the version an open pinned, so an edit between the open and the claim never changes what the
     * purchase pays its creator.
     */
    creatorCutBps: integer("creator_cut_bps").notNull().default(0),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("box_versions_box_version_idx").on(t.boxId, t.version),
    // jsonb_path_ops: the only queries are containment ("which versions hold this item").
    index("box_versions_drop_table_idx").using("gin", t.dropTable.op("jsonb_path_ops")),
  ],
);

/**
 * One row per drop-table entry per version: the shape the processor writes and every reader
 * moves to. `limited_quantity` is the one mutable column; a change to it updates the row in
 * place rather than minting a version, which is why the hash leaves it out.
 */
export const dropTableItems = pgTable(
  "drop_table_items",
  {
    id: text("id").primaryKey(), // ulid
    boxVersionId: text("box_version_id")
      .notNull()
      .references(() => boxVersions.id, { onDelete: "cascade" }),
    /** The sorted index the roll walks. */
    position: integer("position").notNull(),
    itemSource: text("item_source", { enum: DROP_TABLE_ITEM_ROW_SOURCES }).notNull(),
    catalogItemId: uuid("catalog_item_id").references(() => catalogItems.id),
    vendorListingId: text("vendor_listing_id").references(() => vendorListings.id),
    /**
     * A boxed id with no Loot OS row (a STEAM group, an `M#`/`PWCC#` listing). Mirror only: no
     * Loot OS editor can author one and no withdrawal can fulfill one.
     */
    externalItemId: text("external_item_id"),
    /**
     * EXTERNAL only: the source the entry was published with, on a row `backfillDropTableItems`
     * wrote from a legacy version whose id matched no catalog item or vendor listing (a box
     * template's free-form key, a deleted listing). Readers report it in place of EXTERNAL. Null on a
     * row copied from boxed, which was never published in Loot OS.
     */
    publishedSource: text("published_source", { enum: DROP_TABLE_ITEM_PUBLISHED_SOURCES }),
    /**
     * The effective weight the roll walks; a published version's weights sum to exactly 1,000,000.
     * Zero on an entry the processor excluded for having no stock.
     */
    weight: integer("weight").notNull(),
    /**
     * The weight the editor authored, which every processor run starts from, so an entry excluded
     * for having no stock comes back at it once restocked. Differs from `weight` only while an
     * entry of the version is excluded. NULL on a row written before the column, where the two
     * were one. Left out of the hash like `limited_quantity`, and never on the wire.
     */
    authoredWeight: integer("authored_weight"),
    /** The effective value in US cents: what the roll, the EV, a sell-back and the hash read. */
    valueCents: integer("value_cents").notNull(),
    /** The editor's override when set; the hydrate adapter applies it and the processor copies it forward. */
    valueOverrideCents: integer("value_override_cents"),
    /** MARKETPLACE only: the vendor price the withdrawal price check compares against. */
    priceCents: integer("price_cents"),
    name: text("name").notNull(),
    imageUrl: text("image_url").notNull(),
    /** Stamped at write from the stock the entry had then (boxed: `limitedQuantity != null`). */
    isLimited: boolean("is_limited").notNull().default(false),
    /** Remaining stock. Mutable, and left out of the hash with `authored_weight`. */
    limitedQuantity: integer("limited_quantity"),
    isGemSubstitute: boolean("is_gem_substitute").notNull().default(false),
  },
  (t) => [
    uniqueIndex("drop_table_items_version_position_idx").on(t.boxVersionId, t.position),
    index("drop_table_items_catalog_item_idx").on(t.catalogItemId),
    index("drop_table_items_vendor_listing_idx").on(t.vendorListingId),
    check(
      "drop_table_items_source_matches_id",
      sql`(
        (${t.itemSource} = 'CATALOG' AND ${t.catalogItemId} IS NOT NULL AND ${t.vendorListingId} IS NULL AND ${t.externalItemId} IS NULL)
        OR (${t.itemSource} = 'MARKETPLACE' AND ${t.vendorListingId} IS NOT NULL AND ${t.catalogItemId} IS NULL AND ${t.externalItemId} IS NULL)
        OR (${t.itemSource} = 'EXTERNAL' AND ${t.externalItemId} IS NOT NULL AND ${t.catalogItemId} IS NULL AND ${t.vendorListingId} IS NULL)
        OR (${t.itemSource} = 'VOUCHER' AND ${t.catalogItemId} IS NULL AND ${t.vendorListingId} IS NULL AND ${t.externalItemId} IS NULL)
      )`,
    ),
    check(
      "drop_table_items_published_source_external",
      sql`${t.publishedSource} IS NULL OR (${t.itemSource} = 'EXTERNAL' AND ${t.publishedSource} IN ('CATALOG', 'MARKETPLACE'))`,
    ),
  ],
);

/** Writer: tenant editor (boxTemplates.enable/disable). Whole row is safe to return to tenant members. */
export const boxTemplateEnablements = pgTable(
  "box_template_enablements",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (t) => [uniqueIndex("box_template_enablements_env_box_idx").on(t.environmentId, t.boxId)],
);

/**
 * A box an end user saved to their favorites (the storefront's heart). Writer: the end user
 * (`PUT` / `DELETE /v1/me/favorites/{boxId}`). One shopper's bookmark, never a gate on what anyone
 * may read; the whole row is safe to return to that shopper.
 */
export const boxFavorites = pgTable(
  "box_favorites",
  {
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (t) => [
    primaryKey({ columns: [t.endUserId, t.boxId] }),
    // The primary key leads with the user, so a box's delete cascades through this, not a scan.
    index("box_favorites_box_idx").on(t.boxId),
  ],
);

// ---- packs ---------------------------------------------------------------------

/** Which of a pack's three draw buckets an item sits in. */
export enum PackSlotRarity {
  COMMON = "COMMON",
  UNCOMMON = "UNCOMMON",
  RARE = "RARE",
}

export const packSlotRarityEnum = pgEnum("pack_slot_rarity", [
  PackSlotRarity.COMMON,
  PackSlotRarity.UNCOMMON,
  PackSlotRarity.RARE,
]);

/**
 * Writer: the Boxed pack mirror (PUT /packs/{externalId}). A membership record of which catalog
 * items sit in a tenant's packs; Loot OS never opens or sells a pack.
 */
export const packs = pgTable(
  "packs",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** The tenant's pack id. */
    externalId: text("external_id").notNull(),
    name: text("name").notNull(),
    /** Null when the tenant's pack has no image. */
    imageUrl: text("image_url"),
    status: text("status", { enum: ["draft", "active", "archived"] }).notNull(),
    priceCents: integer("price_cents").notNull(),
    cardCount: integer("card_count").notNull(),
    commonCount: integer("common_count").notNull(),
    uncommonCount: integer("uncommon_count").notNull(),
    rareCount: integer("rare_count").notNull(),
    /** Chance out of 1,000,000 that a pack is a god pack, which draws every slot from rare. */
    godPackWeight: integer("god_pack_weight").notNull(),
    /** Hash of the stored pack_items; the mirror skips a write whose items hash the same. */
    contentHash: text("content_hash").notNull(),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("packs_tenant_external_idx").on(t.tenantId, t.externalId),
    index("packs_active_idx").on(t.status).where(sql`${t.status} = 'active'`),
    check(
      "packs_counts_non_negative",
      sql`${t.cardCount} >= 0 AND ${t.commonCount} >= 0 AND ${t.uncommonCount} >= 0 AND ${t.rareCount} >= 0`,
    ),
    check(
      "packs_card_count_sum",
      sql`${t.cardCount} = ${t.commonCount} + ${t.uncommonCount} + ${t.rareCount}`,
    ),
    check("packs_god_pack_weight_range", sql`${t.godPackWeight} BETWEEN 0 AND 1000000`),
  ],
);

/** One row per item per bucket of a mirrored pack. */
export const packItems = pgTable(
  "pack_items",
  {
    id: text("id").primaryKey(), // ulid
    packId: text("pack_id")
      .notNull()
      .references(() => packs.id, { onDelete: "cascade" }),
    slotRarity: packSlotRarityEnum("slot_rarity").notNull(),
    /** Order inside the bucket, as the tenant sent it. */
    position: integer("position").notNull(),
    catalogItemId: uuid("catalog_item_id").references(() => catalogItems.id),
    /** The tenant's item id, set only when no catalog row visible to the tenant matches it. */
    externalItemId: text("external_item_id"),
    weight: integer("weight").notNull(),
    valueCents: integer("value_cents").notNull(),
    name: text("name").notNull(),
    imageUrl: text("image_url").notNull().default(""),
    printedRarity: text("printed_rarity"),
    isGemSubstitute: boolean("is_gem_substitute").notNull().default(false),
  },
  (t) => [
    uniqueIndex("pack_items_pack_slot_position_idx").on(t.packId, t.slotRarity, t.position),
    index("pack_items_catalog_item_idx").on(t.catalogItemId),
    check(
      "pack_items_one_item_id",
      sql`(${t.catalogItemId} IS NULL) <> (${t.externalItemId} IS NULL)`,
    ),
    check("pack_items_weight_non_negative", sql`${t.weight} >= 0`),
    check("pack_items_value_non_negative", sql`${t.valueCents} >= 0`),
  ],
);

// ---- provably fair -----------------------------------------------------------

export const serverSeeds = pgTable("server_seeds", {
  /** UTC date the seed applies to, formatted YYYY-MM-DD. */
  date: char("date", { length: 10 }).primaryKey(),
  seed: text("seed").notNull(),
  seedHash: text("seed_hash").notNull(),
  ...timestamps,
});

// ---- open groups (reveal mode) ---------------------------------------------------

/**
 * One row per open request — the claimable unit of reveal mode. An open reveals
 * items without debiting the balance; the debit happens at claim, as a purchase of the
 * already-revealed items at this row's snapshotted price. Claimed rows are kept
 * (never deleted): reveal createdAt < claimedAt is the compliance audit trail.
 *
 * Writer: the public /v1 open and claim flow, on behalf of one end user. Safe to return to that end user's tenant.
 */
export const openGroups = pgTable(
  "open_groups",
  {
    id: text("id").primaryKey(), // ulid; the openGroupId on opens rows
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id),
    boxVersionId: text("box_version_id")
      .notNull()
      .references(() => boxVersions.id),
    /** Total purchase price in US cents, snapshotted at reveal time. */
    costCents: integer("cost_cents").notNull(),
    status: text("status", { enum: ["unclaimed", "claimed"] })
      .notNull()
      .default("unclaimed"),
    claimedAt: timestamp("claimed_at", { withTimezone: true }),
    /**
     * Set together by a sell-back (`sellBackReveal`): the sale that followed this group's claim and
     * what it credited. A sold group is also `claimed`; null means the items were kept.
     */
    soldAt: timestamp("sold_at", { withTimezone: true }),
    soldCents: integer("sold_cents"),
    /** The OPEN request's Idempotency-Key; replay reconstructs from this group. */
    idempotencyKey: text("idempotency_key"),
    ...timestamps,
  },
  (t) => [
    // The one-open-at-a-time gate: a user may hold at most one unclaimed open group. Battles add
    // unclaimed battle_players seats to the same gate (several at once are allowed there);
    // claims/pendingReveal.ts is the one check that reads both.
    uniqueIndex("open_groups_unclaimed_one_idx")
      .on(t.endUserId)
      .where(sql`${t.status} = 'unclaimed'`),
    uniqueIndex("open_groups_idempotency_idx")
      .on(t.environmentId, t.idempotencyKey)
      .where(sql`${t.idempotencyKey} IS NOT NULL`),
    index("open_groups_end_user_idx").on(t.endUserId, t.createdAt),
  ],
);

// ---- opens ----------------------------------------------------------------------

/** Writer: the public /v1 open flow. Safe to return; the day's server seed stays in server_seeds until it is revealed. */
export const opens = pgTable(
  "opens",
  {
    id: text("id").primaryKey(), // ulid
    /** Groups the N rolls of a single multi-open request. */
    openGroupId: text("open_group_id")
      .notNull()
      .references(() => openGroups.id),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id),
    boxVersionId: text("box_version_id")
      .notNull()
      .references(() => boxVersions.id),
    clientSeed: varchar("client_seed", { length: 36 }).notNull(),
    nonce: integer("nonce").notNull(),
    /** resultNumber in [0, 999999]. */
    result: integer("result").notNull(),
    itemIndex: integer("item_index").notNull(),
    item: jsonb("item").$type<DropTableItem>().notNull(),
    costCents: integer("cost_cents").notNull(),
    /** Which daily server seed produced this roll. */
    seedDate: char("seed_date", { length: 10 })
      .notNull()
      .references(() => serverSeeds.date),
    ...timestamps,
  },
  (t) => [
    // Provably-fair integrity backstop: a nonce is consumed at most once per user.
    uniqueIndex("opens_user_nonce_idx").on(t.tenantId, t.endUserId, t.nonce),
    index("opens_tenant_created_idx").on(t.tenantId, t.createdAt),
    index("opens_env_created_idx").on(t.environmentId, t.createdAt),
    index("opens_group_idx").on(t.openGroupId),
    index("opens_end_user_idx").on(t.endUserId, t.createdAt),
  ],
);

// ---- balance ledger -------------------------------------------------------------

/**
 * Every kind of balance movement. `credit` is funding (a tenant's server or staff crediting the
 * user; `computeTrustTier` reads the first one as the first funding event), `claim_debit` a
 * purchase of revealed items, `battle_entry` the battle price debited when a seat is taken
 * (create or join; `ref` = the battle id, and the claim then moves nothing), `adjustment` a staff
 * correction, `shipping_fee` the shipping cost debited when a withdrawal order is created,
 * `shipping_fee_refund` that fee handed back when the order is cancelled (its own type, so a
 * refund never reads as funding) and `sell_credit` the value credited when claimed items are sold
 * back to the house (its own type for the same reason; a sell-back of an open group writes it
 * right after its `claim_debit`, see `sellInventoryItemsInTx`) and `forge_commission` a forge
 * box creator's accrued cut paid into their balance (`claimForgeEarnings`; its own type so a
 * payout never reads as funding) and `store_credit_out` value leaving Loot OS for a shopper's
 * Shopify store credit (`core/shopify`, `debitToStoreCreditInTx`; negative, the one writer) and
 * `gift_code` a gift code's amount credited when a shopper redeems it (`redeemGiftCode`; its own
 * type so a giveaway never reads as funding).
 */
export const BALANCE_LEDGER_TYPES = [
  "credit",
  "claim_debit",
  "battle_entry",
  "adjustment",
  "shipping_fee",
  "shipping_fee_refund",
  "sell_credit",
  "forge_commission",
  "store_credit_out",
  "gift_code",
] as const;
export type BalanceLedgerType = (typeof BALANCE_LEDGER_TYPES)[number];

/** Writer: the public /v1 balance flow and the Stripe webhooks. Whole row is safe to return to the owning tenant. */
export const balanceLedger = pgTable(
  "balance_ledger",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    /** Signed US cents: positive = credit, negative = debit. */
    amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
    balanceAfterCents: bigint("balance_after_cents", { mode: "number" }).notNull(),
    type: text("type", { enum: BALANCE_LEDGER_TYPES }).notNull(),
    /** Reference to the causing record (open_group_id, dashboard action, etc.). */
    ref: text("ref"),
    idempotencyKey: text("idempotency_key"),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("balance_ledger_idempotency_idx")
      .on(t.environmentId, t.idempotencyKey)
      .where(sql`${t.idempotencyKey} IS NOT NULL`),
    index("balance_ledger_end_user_idx").on(t.endUserId, t.createdAt),
  ],
);

// ---- inventory ------------------------------------------------------------------

/**
 * Writer: the public /v1 claim and sell-back flows (open groups) and claimBattleWinnings /
 * sellBackBattleWinnings (battles); `status` and `sold_at` also by sellInventoryItems. Whole row is
 * safe to return to the owning tenant. Provenance is exactly one of `open_id` (a solo open),
 * `battle_open_id` (the battle open whose item this seat was awarded), `battle_player_id` (the
 * seat a battle loser prize was minted for) or `refunded_inventory_item_id` (the unit a
 * cancelled marketplace order paid back as a voucher).
 */
/**
 * `owned` after a claim; `fulfillment_requested` once an order names the unit; `sold` once a
 * sell-back credited its value; `refunded` once a cancelled marketplace order replaced the unit
 * with a voucher. The last two are terminal and the row is kept: it is the audit trail of the
 * sale or the refund. Declared in `@loot-os/api-contract` because the wire carries it too;
 * re-exported here so the tables and their readers keep one import.
 */
export { INVENTORY_ITEM_STATUSES, type InventoryItemStatus };

export const inventoryItems = pgTable(
  "inventory_items",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    openId: text("open_id").references(() => opens.id),
    battleOpenId: text("battle_open_id").references((): AnyPgColumn => battleOpens.id),
    /** The seat a battle loser prize was minted for: its provenance is the seat, not an open. */
    battlePlayerId: text("battle_player_id").references((): AnyPgColumn => battlePlayers.id),
    /** The unit a cancelled marketplace order refunded: this row is the voucher that replaced it. */
    refundedInventoryItemId: text("refunded_inventory_item_id").references(
      (): AnyPgColumn => inventoryItems.id,
    ),
    item: jsonb("item").$type<DropTableItem>().notNull(),
    status: text("status", { enum: INVENTORY_ITEM_STATUSES }).notNull().default("owned"),
    /** When a sell-back credited this unit's value; set with `status = 'sold'`. */
    soldAt: timestamp("sold_at", { withTimezone: true }),
    ...timestamps,
  },
  (t) => [
    index("inventory_items_end_user_idx").on(t.endUserId, t.createdAt),
    // Claim idempotency backstop: an open materializes at most one inventory item.
    uniqueIndex("inventory_items_open_idx").on(t.openId),
    uniqueIndex("inventory_items_battle_open_idx")
      .on(t.battleOpenId)
      .where(sql`${t.battleOpenId} IS NOT NULL`),
    // The same backstop for the loser prize: a seat is minted at most one.
    uniqueIndex("inventory_items_battle_player_idx")
      .on(t.battlePlayerId)
      .where(sql`${t.battlePlayerId} IS NOT NULL`),
    // The same backstop for the refund: a cancelled unit buys at most one voucher.
    uniqueIndex("inventory_items_refunded_idx")
      .on(t.refundedInventoryItemId)
      .where(sql`${t.refundedInventoryItemId} IS NOT NULL`),
    check(
      "inventory_items_provenance_exactly_one",
      sql`((${t.openId} IS NOT NULL)::int + (${t.battleOpenId} IS NOT NULL)::int + (${t.battlePlayerId} IS NOT NULL)::int + (${t.refundedInventoryItemId} IS NOT NULL)::int) = 1`,
    ),
  ],
);

// ---- fulfillment intents (the withdrawal saga) -------------------------------------
// docs/fulfillment/withdrawal-saga.md. A port of boxed's FulfillmentIntent table.

/**
 * RESERVED: written before the Fulfillment API is called; the call may or may not have landed.
 * COMMITTED: the Fulfillment API accepted and loot-os's account side is written.
 * FAILED: the Fulfillment API holds nothing for this step (refused, released, or never cancelled).
 * ABANDONED: the reconcile sweep gave up or retired it; a later request may revive it.
 */
export const FULFILLMENT_INTENT_STATUSES = [
  "RESERVED",
  "COMMITTED",
  "FAILED",
  "ABANDONED",
] as const;
export type FulfillmentIntentStatus = (typeof FULFILLMENT_INTENT_STATUSES)[number];
export const FULFILLMENT_INTENT_FLOWS = ["CATALOG_ITEM_WITHDRAW", "WITHDRAW_MARKETPLACE"] as const;
export type FulfillmentIntentFlow = (typeof FULFILLMENT_INTENT_FLOWS)[number];
export const FULFILLMENT_INTENT_OPERATIONS = ["CREATE", "APPEND", "CANCEL_ORDER"] as const;
export type FulfillmentIntentOperation = (typeof FULFILLMENT_INTENT_OPERATIONS)[number];

/** The refund a cancel will pay, frozen when the cancel is asked for (boxed's cancel plan). */
export interface FulfillmentCancelPlan {
  refundCents: number;
  reason: string | null;
  code: "TENANT_CANCELLED" | "TENANT_ADMIN_CANCELLED";
}

/**
 * One step of a withdrawal saga: a create, a join (append) or a cancel. A create or join is written
 * RESERVED together with loot-os's account side (the fee charged, the units reserved) before the
 * Fulfillment API is called; a cancel is written RESERVED before the call and settled after.
 * Writer: the withdrawal saga and its reconcile sweep only. Never returned to a tenant;
 * `last_error` may carry an upstream body.
 */
export const fulfillmentIntents = pgTable(
  "fulfillment_intents",
  {
    /** The intent key (the request key, or `{key}#APPEND#{orderId}`, `{orderId}#CANCEL_ORDER`). */
    id: text("id").primaryKey(),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    status: text("status", { enum: FULFILLMENT_INTENT_STATUSES }).notNull().default("RESERVED"),
    flow: text("flow", { enum: FULFILLMENT_INTENT_FLOWS }).notNull(),
    operation: text("operation", { enum: FULFILLMENT_INTENT_OPERATIONS }).notNull(),
    /** The withdrawal request's key (the item-set key); a retry finds its earlier steps by it. */
    requestKey: text("request_key").notNull(),
    /** The Fulfillment API's order id: set on commit (and known up front for APPEND / CANCEL_ORDER). */
    orderId: text("order_id"),
    inventoryItemIds: text("inventory_item_ids").array().notNull().default([]),
    shippingFeeCents: integer("shipping_fee_cents").notNull().default(0),
    cancelPlan: jsonb("cancel_plan").$type<FulfillmentCancelPlan>(),
    lastError: text("last_error"),
    lastHttpStatus: integer("last_http_status"),
    lastAttemptAt: timestamp("last_attempt_at", { withTimezone: true }),
    attemptCount: integer("attempt_count").notNull().default(0),
    releaseCount: integer("release_count").notNull().default(0),
    /**
     * Unique per attempt: written fresh on every reserve and every revival, and the nonce of the
     * Idempotency-Key and the fee's ledger keys. A step id repeats (it is the request key, and a
     * pruned step can be written again), so the keys never derive from the id alone.
     */
    attemptNonce: text("attempt_nonce").notNull(),
    /** A committed CREATE's order: when the sweep last read it from the Fulfillment API. */
    orderCheckedAt: timestamp("order_checked_at", { withTimezone: true }),
    /**
     * A committed CREATE's order reached a final status (shipped or cancelled) and loot-os applied
     * what follows from it; the sweep stops reading it.
     */
    orderSettledAt: timestamp("order_settled_at", { withTimezone: true }),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // The reconcile sweep's read: the in-flight steps only, longest idle first. Committed steps are
    // kept forever and never swept, so they stay out of this index.
    index("fulfillment_intents_status_updated_idx")
      .on(t.status, t.updatedAt)
      .where(sql`${t.status} IN ('RESERVED', 'FAILED')`),
    // The sweep's order poll: committed creates whose order is not final, least recently read.
    index("fulfillment_intents_open_orders_idx")
      .on(t.orderCheckedAt)
      .where(
        sql`${t.status} = 'COMMITTED' AND ${t.operation} = 'CREATE' AND ${t.orderSettledAt} IS NULL`,
      ),
    index("fulfillment_intents_order_idx").on(t.orderId),
    // A retried request finds its earlier steps (`resumeWithdrawal`).
    index("fulfillment_intents_request_idx").on(t.environmentId, t.requestKey),
  ],
);

// ---- hosted storefront -----------------------------------------------------------
// One shared storefront serves every tenant: the request Host resolves to a tenant via
// storefront_domains, and storefront_configs decides how that tenant's site renders.

export const STOREFRONT_DOMAIN_TYPES = ["subdomain", "custom"] as const;

/**
 * A custom domain's life: pending (no edge tenant yet) → provisioning (CloudFront tenant
 * exists, waiting on DNS and the certificate) → active. failed carries last_error and is
 * retried; disabled is the staff switch. Provisioned subdomains are born active.
 */
export const STOREFRONT_DOMAIN_STATUSES = [
  "pending",
  "provisioning",
  "active",
  "failed",
  "disabled",
] as const;
export type StorefrontDomainStatus = (typeof STOREFRONT_DOMAIN_STATUSES)[number];

/** What CloudFront reports for a managed certificate, mirrored on the row for display. */
export const EDGE_CERTIFICATE_STATUSES = [
  "pending-validation",
  "issued",
  "inactive",
  "expired",
  "validation-timed-out",
  "revoked",
  "failed",
] as const;
export type EdgeCertificateStatus = (typeof EDGE_CERTIFICATE_STATUSES)[number];

/**
 * Where a custom domain's branded sender (`no-reply@{hostname}`) stands with SES: pending until
 * the tenant's DKIM CNAMEs resolve, verified once SES will sign and send from it, failed when
 * SES gave up looking for the records (Retry requests a fresh identity). NULL on the row means no
 * identity has been requested yet. Only `verified` moves the sender off the platform address.
 */
export const STOREFRONT_EMAIL_STATUSES = ["pending", "verified", "failed"] as const;
export type StorefrontEmailStatus = (typeof STOREFRONT_EMAIL_STATUSES)[number];

/**
 * 1:1 with environments: every env (the live one and each sandbox) renders its
 * own independent storefront. The environment decides which data the site
 * serves; the tenant's slug is the only tenant-level bit read in.
 *
 * Writer: tenant editor (storefront.update, storefront.setShippingDestinations,
 * storefront.setLoserPrizeImage, storefront.setLegal, storefront.setTermly,
 * storefront.setTermlyTheme, openerStyles.setEnvironmentDefault, openerStyles.setLogo), except
 * `enabled`, which only platform staff set. Whole row is safe to return. The Intercom app ids
 * are stage config, never on this row; the platform's default shipping destinations, loser prize
 * image, legal identity and Termly website live on `platform_settings`.
 */
export const storefrontConfigs = pgTable(
  "storefront_configs",
  {
    environmentId: text("environment_id")
      .primaryKey()
      .references(() => environments.id, { onDelete: "cascade" }),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    /** The config endpoint 404s (site dark) until staff flip this on. */
    enabled: boolean("enabled").notNull().default(false),
    /** Falls back to tenant.name when empty. */
    displayName: text("display_name").notNull().default(""),
    logoUrl: text("logo_url").notNull().default(""),
    faviconUrl: text("favicon_url").notNull().default(""),
    /** Wide merchandising image; empty until staff upload or paste a URL. */
    bannerUrl: text("banner_url").notNull().default(""),
    /** Small mark distinct from the logo (packs, seats); empty until set. */
    badgeUrl: text("badge_url").notNull().default(""),
    /**
     * The display currency (presentation only; every amount is stored in US cents). Credits
     * by default: name "credit", symbol "$", multiplier 100, so 1,250 cents reads "$12.50".
     * Resolved onto the wire by api-contract's `resolveCurrency`.
     */
    currencyName: text("currency_name").notNull().default("credit"),
    /** null = derive `${currencyName}s`. */
    currencyNamePlural: text("currency_name_plural"),
    /** Prefix before the amount; "" shows the unit name instead ("1,250 gems"). */
    currencySymbol: text("currency_symbol").notNull().default("$"),
    /** Internal cents per displayed whole unit: 1, 10, 100 or 1000 (CURRENCY_MULTIPLIERS). */
    currencyMultiplier: integer("currency_multiplier").notNull().default(100),
    /**
     * Uploaded currency icon as SVG source, "" for none. Validated on ingest and rendered
     * only through an <img> data URI — see api-contract's svgIcon.ts before touching either end.
     */
    currencyIconSvg: text("currency_icon_svg").notNull().default(""),
    /**
     * The tenant's per-environment switch for Intercom live chat. Effective only on a stage
     * with a workspace for this environment's mode (`INTERCOM_LIVE_*` for the live environment,
     * `INTERCOM_TEST_*` for a sandbox): the resolver folds both into `intercom.appId` on the wire
     * ("" = off), so the SPA has one thing to check.
     */
    intercomEnabled: boolean("intercom_enabled").notNull().default(false),
    /**
     * Uploaded chat-launcher icon as SVG source, "" for the storefront's default glyph. Same
     * rules as currencyIconSvg: validated on ingest and rendered only through an <img> data URI —
     * see api-contract's svgIcon.ts before touching either end.
     */
    intercomLauncherIconSvg: text("intercom_launcher_icon_svg").notNull().default(""),
    theme: jsonb("theme").$type<StorefrontTheme>().notNull().default({}),
    /** Overrides only; merged onto STOREFRONT_FEATURE_DEFAULTS at read time. */
    features: jsonb("features")
      .$type<Partial<Record<StorefrontFeatureKey, boolean>>>()
      .notNull()
      .default({}),
    /**
     * This environment's own shipping destinations (`ShippingDestination[]`, api-contract), in
     * display order. NULL = inherit the platform default on `platform_settings`; `[]` = this
     * environment ships nowhere. Read only through `parseShippingDestinations`, never as-is: the
     * list decides a fee.
     */
    shippingDestinations: jsonb("shipping_destinations"),
    /**
     * When the destinations were last saved: the optimistic-lock token
     * `storefront.setShippingDestinations` echoes back, kept apart from `updated_at` so a
     * branding save never conflicts with a shipping save. NULL = never customized.
     */
    shippingUpdatedAt: timestamp("shipping_updated_at", { withTimezone: true }),
    /**
     * The image shoppers see for the battle loser prize (`BATTLE_LOSER_PRIZE_ITEM`), as a URL.
     * NULL = inherit the platform default on `platform_settings`; set = this environment's own.
     * Read through `resolveLoserPrizeImageUrl` and snapshotted onto `battles.loser_prize_item`
     * when a battle is created, so a later change never rewrites an existing battle.
     */
    loserPrizeImageUrl: text("loser_prize_image_url"),
    /**
     * The opener style this environment's boxes open with unless a box or shop names its own.
     * NULL = inherit the platform default of the channel it opens on
     * (`platform_settings.default_opener_style_id`, or `shopify_default_opener_style_id` in a
     * Shopify shop's overlay). Read through `resolveOpenerStyle`.
     */
    openerStyleId: text("opener_style_id").references((): AnyPgColumn => boxOpenerStyles.id),
    /**
     * The tenant's logo as SVG source, "" for none, drawn onto every opener as its brand (the
     * Neon Slam crate, the Neon Pack wrapper, the Retail Box print, above the spinner's reel).
     * Written from the Openers page only (`openerStyles.setLogo`); no platform default. Same rules
     * as currencyIconSvg: validated on ingest (`normalizeSvgIcon`) and rendered only through an
     * <img> or an Image() data URI, never inline — see api-contract's svgIcon.ts.
     */
    openerLogoSvg: text("opener_logo_svg").notNull().default(""),
    /**
     * The tenant's name, "" for none, printed as text where an opener draws the logo when there
     * is no logo (`openerLogoSvg` wins when both are set). Written from the Openers page only
     * (`openerStyles.setBrandName`); no platform default. Public: every shopper sees it.
     */
    openerBrandName: text("opener_brand_name").notNull().default(""),
    /**
     * This environment's own legal identity (`StorefrontLegal`, api-contract): the entity,
     * contacts and governing law its Terms of Service and Privacy Policy name. NULL = inherit
     * the platform default on `platform_settings`. Read only through `parseStorefrontLegal`,
     * never as-is: the pages print it into a contract.
     */
    legal: jsonb("legal"),
    /**
     * This environment's own Termly website for cookie consent, as the UUID in Termly's embed
     * snippet. NULL = inherit the platform default on `platform_settings`; set = the tenant's own
     * Termly website records this storefront's consents. Public: it is in every page's script
     * URL. Read through `resolveTermlyWebsiteUuid`.
     */
    termlyWebsiteUuid: text("termly_website_uuid"),
    /**
     * This environment's own cookie banner theme (`CookieBannerTheme`, api-contract): the colors
     * and radius Termly's banner and preference modal take here. NULL = inherit the platform
     * default on `platform_settings`; a token left out of a set theme follows the store's own
     * palette. Read only through `parseCookieBannerTheme`, never as-is.
     */
    cookieBannerTheme: jsonb("cookie_banner_theme"),
    /**
     * The tenant's social profiles (`SocialLinks`, api-contract), keyed by network with an
     * `https` URL each, shown as icons in the storefront footer. `{}` = no icons; there is no
     * platform default, because a store's socials are its own. Written by a tenant editor
     * (`storefront.setSocialLinks`) and public on every page, so nothing here is sensitive. Read
     * only through `parseSocialLinks`, never as-is: every value becomes an `href`.
     */
    socialLinks: jsonb("social_links").notNull().default({}),
    /**
     * The storefront's own words for its nouns (`StorefrontTerminologyOverrides`, api-contract):
     * an override per term the tenant renamed, so `{}` = every default. Written by a tenant
     * editor (`storefront.setTerminology`) and public on every page, so nothing here is
     * sensitive. Read only through `parseStorefrontTerminology`, never as-is.
     */
    terminology: jsonb("terminology").notNull().default({}),
    /**
     * The tenant's edits of the system notifications (`NotificationTemplateOverrides`,
     * api-contract): an override per edited type, so `{}` = every default. Written by a tenant
     * editor (`notifications.updateTemplate` / `resetTemplate`); shoppers see it only rendered.
     * Read only through `parseNotificationTemplateOverrides`, never as-is.
     */
    notificationTemplates: jsonb("notification_templates").notNull().default({}),
    /**
     * The storefront template this environment's home page renders (`StorefrontTemplateId`,
     * api-contract). Text rather than an enum, so adding a template needs no migration. Written by
     * a tenant editor (`storefront.setTemplate`) and public. Read only through
     * `parseStorefrontTemplateId`, never as-is.
     */
    storefrontTemplate: text("storefront_template").notNull().default("classic"),
    /**
     * The environment's forge settings (`ForgeSettings`, api-contract): whether creators earn a
     * commission, the largest cut one may pick, and the preset box images a forge box chooses
     * from. `{}` = the defaults (commission on, 3% max, no images). Written by a tenant editor
     * (`setForgeSettings`, which reprices every forge box in the same transaction) and public on
     * every page. Read only through `parseForgeSettings`, never as-is: it prices boxes.
     */
    forgeSettings: jsonb("forge_settings").notNull().default({}),
    /**
     * The environment's internal publishable key, plaintext. Publishable keys
     * are public by design; storing this one lets the BFF proxy inject it per
     * request, and its row id doubles as the shopper session JWTs' kid
     * (per-environment kill switch).
     */
    publishableKey: text("publishable_key").notNull().default(""),
    publishableKeyId: text("publishable_key_id").references(() => apiKeys.id),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("storefront_configs_tenant_idx").on(t.tenantId),
    check(
      "storefront_configs_currency_multiplier_allowed",
      sql`${t.currencyMultiplier} IN (1, 10, 100, 1000)`,
    ),
  ],
);

/**
 * Platform-wide settings, one row (`id = 'platform'`), created on first save or first shipping
 * read (`ensurePlatformSettingsRow`). Writer: platform staff (`platform.shipping.set`,
 * `platform.loserPrize.set`, `platform.legal.set`, `platform.termly.set`,
 * `platform.termly.setTheme`, `platform.openerStyles.setDefault`,
 * `platform.openerStyles.setShopifyDefault`). Whole row is safe to return to any dashboard
 * member: it holds the defaults every environment inherits, nothing tenant-specific.
 */
export const platformSettings = pgTable("platform_settings", {
  id: text("id").primaryKey(),
  /**
   * The default shipping destinations (`ShippingDestination[]`, api-contract) every environment
   * without its own `storefront_configs.shipping_destinations` uses, in display order. Read only
   * through `parseShippingDestinations`. `[]` = shipping is off wherever nothing overrides it.
   */
  shippingDestinations: jsonb("shipping_destinations").notNull().default([]),
  /** Optimistic-lock token for `platform.shipping.set`; NULL until the first save. */
  shippingUpdatedAt: timestamp("shipping_updated_at", { withTimezone: true }),
  /**
   * The battle loser prize's image URL wherever no `storefront_configs.loser_prize_image_url`
   * overrides it. "" = none: the storefront shows its placeholder.
   */
  loserPrizeImageUrl: text("loser_prize_image_url").notNull().default(""),
  /**
   * The legal identity (`StorefrontLegal`, api-contract) every environment's Terms of Service
   * and Privacy Policy name until its own `storefront_configs.legal` overrides it. NULL = the
   * platform's own identity as `PLATFORM_LEGAL_DEFAULT` carries it, so a fresh stage never
   * renders a contract with blanks. Read only through `parseStorefrontLegal`.
   */
  legal: jsonb("legal"),
  /**
   * Whether a Fanatics withdrawal is BUY_BOT and creates its `FANATICS_BUY` job (boxed's
   * `buyWithFanaticsBot` config value). False makes the withdrawal MANUAL_BUY, for a human to
   * buy from the manual-buy queue, which is what every stage does until the bot polls the Loot
   * OS Job API rather than boxed's own: while boxed still runs `createFanaticsBuyJob` for the
   * orders it mirrors here, turning this on would buy each of them twice.
   */
  buyWithFanaticsBot: boolean("buy_with_fanatics_bot").notNull().default(false),
  /**
   * The Termly website whose cookie banner every storefront shows until its own
   * `storefront_configs.termly_website_uuid` overrides it. "" = none: no banner and nothing
   * loaded from Termly wherever nothing overrides it (a stage without a Termly website).
   */
  termlyWebsiteUuid: text("termly_website_uuid").notNull().default(""),
  /**
   * The cookie banner theme (`CookieBannerTheme`, api-contract) every storefront's banner takes
   * until its own `storefront_configs.cookie_banner_theme` overrides it. NULL = nothing set: every
   * token follows each store's own palette. Read only through `parseCookieBannerTheme`.
   */
  cookieBannerTheme: jsonb("cookie_banner_theme"),
  /**
   * The storefront default: the opener style a box opens with everywhere but a Shopify shop's
   * overlay (the hosted storefront, the /v1 API and @loot-os/react embeds) until the box or its
   * environment names its own. NULL = `DEFAULT_OPENER_STYLE_ID`, the seeded "Classic spinner". A
   * platform style only.
   */
  defaultOpenerStyleId: text("default_opener_style_id").references(
    (): AnyPgColumn => boxOpenerStyles.id,
  ),
  /**
   * The Shopify default: the opener style a box opens with in a Shopify shop's overlay until the
   * box, the shop or its environment names its own. NULL = the storefront default
   * (`default_opener_style_id`). A platform style only.
   */
  shopifyDefaultOpenerStyleId: text("shopify_default_opener_style_id").references(
    (): AnyPgColumn => boxOpenerStyles.id,
  ),
  ...timestamps,
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

/**
 * Writer: tenant admin adds, retries and removes hostnames; the edge reconcile loop (dashboard
 * reads + the StorefrontDomainsReconcile cron) writes status and the edge_* / certificate
 * columns, and the email_* columns from what SES reports about the hostname's sender identity;
 * platform staff flip enabled/disabled. Whole row is safe to return: last_error and
 * email_last_error are our own phrasing, never raw AWS text, and the DKIM tokens are the public
 * half of the records the tenant adds.
 */
export const storefrontDomains = pgTable(
  "storefront_domains",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    /** The environment this hostname renders (live storefront or a sandbox). */
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    hostname: text("hostname").notNull(),
    /** subdomain = provisioned under ROOT_DOMAIN (irremovable); custom = tenant-attached. */
    type: text("type", { enum: STOREFRONT_DOMAIN_TYPES }).notNull(),
    /** See STOREFRONT_DOMAIN_STATUSES; the resolver serves `active` rows only. */
    status: text("status", { enum: STOREFRONT_DOMAIN_STATUSES }).notNull().default("pending"),
    verifiedAt: timestamp("verified_at", { withTimezone: true }),
    isPrimary: boolean("is_primary").notNull().default(false),
    /** The CloudFront distribution tenant serving this hostname, once one has been requested. */
    edgeTenantId: text("edge_tenant_id"),
    /**
     * The multi-tenant distribution that tenant belongs to. Personal stages share the dev
     * database but each has its own distribution, so this decides which stage reconciles the row.
     */
    edgeDistributionId: text("edge_distribution_id"),
    /** CloudFront's managed-certificate status at the last reconcile, for display. */
    certificateStatus: text("certificate_status", { enum: EDGE_CERTIFICATE_STATUSES }),
    /** Why the row is failed, in our own words. */
    lastError: text("last_error"),
    /** Last reconcile against CloudFront. */
    edgeCheckedAt: timestamp("edge_checked_at", { withTimezone: true }),
    /** When the current edge tenant was created: the retry clock for a timed-out validation. */
    edgeProvisionedAt: timestamp("edge_provisioned_at", { withTimezone: true }),
    /** See STOREFRONT_EMAIL_STATUSES; null until a sender identity has been requested. */
    emailStatus: text("email_status", { enum: STOREFRONT_EMAIL_STATUSES }),
    /**
     * The Easy DKIM tokens SES issued for `no-reply@mail.{hostname}`: each is a CNAME the tenant
     * adds (`{token}._domainkey.mail.{hostname}` → `{token}.dkim.amazonses.com`). Set when the
     * identity is requested, which is what puts the records in the DNS plan.
     */
    emailDkimTokens: text("email_dkim_tokens").array(),
    /** What the sender is waiting on, or why it failed, in our own words. */
    emailLastError: text("email_last_error"),
    /** Last reconcile against SES. */
    emailCheckedAt: timestamp("email_checked_at", { withTimezone: true }),
    emailVerifiedAt: timestamp("email_verified_at", { withTimezone: true }),
    ...timestamps,
  },
  (t) => [
    // Hostnames are globally unique — a host resolves to exactly one environment.
    uniqueIndex("storefront_domains_hostname_idx").on(t.hostname),
    uniqueIndex("storefront_domains_primary_idx").on(t.environmentId).where(sql`${t.isPrimary}`),
    // One row per edge tenant, so a reconcile can never adopt a tenant twice.
    uniqueIndex("storefront_domains_edge_tenant_idx")
      .on(t.edgeTenantId)
      .where(sql`${t.edgeTenantId} IS NOT NULL`),
    index("storefront_domains_tenant_idx").on(t.tenantId),
    index("storefront_domains_env_idx").on(t.environmentId),
    check("storefront_domains_hostname_lower", sql`${t.hostname} = lower(${t.hostname})`),
  ],
);

/**
 * Environment-scoped shopper identity for the hosted storefront: the same email
 * gets an independent account per environment (live vs each sandbox). Each
 * shopper maps 1:1 to an end_users row in its environment (externalId
 * "shopper_<id>"), so opens/claims/balances reuse the existing rails.
 *
 * Writer: the hosted storefront sign-in flow. Safe to return to the shopper it describes and to the owning tenant.
 */
export const storefrontShoppers = pgTable(
  "storefront_shoppers",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    email: text("email").notNull(),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    lastLoginAt: timestamp("last_login_at", { withTimezone: true }),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("storefront_shoppers_env_email_idx").on(t.environmentId, t.email),
    uniqueIndex("storefront_shoppers_end_user_idx").on(t.endUserId),
    index("storefront_shoppers_tenant_idx").on(t.tenantId),
    check("storefront_shoppers_email_lower", sql`${t.email} = lower(${t.email})`),
  ],
);

/**
 * Single-use magic-link tokens; sha256 at rest, consumed via guarded UPDATE.
 *
 * Writer: the hosted storefront magic-link flow. token_hash must never reach any response, and
 * neither may client_ip, user_agent or device_session: they are captured from the shopper's
 * browser when the link is requested and read once, at verify, to screen a new sign-up.
 */
export const storefrontLoginTokens = pgTable(
  "storefront_login_tokens",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    /** The same email can hold concurrent pending links for live and a sandbox. */
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    email: text("email").notNull(),
    tokenHash: text("token_hash").notNull(),
    redirectPath: text("redirect_path"),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    consumedAt: timestamp("consumed_at", { withTimezone: true }),
    /** The requesting browser's IP as the BFF saw it; a fraud signal, never authorization. */
    clientIp: text("client_ip"),
    userAgent: text("user_agent"),
    /** Opaque SEON JS-agent session, forwarded verbatim to SEON and nulled after use. */
    deviceSession: text("device_session"),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("storefront_login_tokens_hash_idx").on(t.tokenHash),
    index("storefront_login_tokens_tenant_email_idx").on(t.tenantId, t.email),
  ],
);

/**
 * Long-lived shopper sessions (the BFF's cookie value); sha256 at rest.
 *
 * Writer: the hosted storefront magic-link flow. token_hash must never reach any response.
 */
export const storefrontShopperSessions = pgTable(
  "storefront_shopper_sessions",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    shopperId: text("shopper_id")
      .notNull()
      .references(() => storefrontShoppers.id, { onDelete: "cascade" }),
    tokenHash: text("token_hash").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    lastSeenAt: timestamp("last_seen_at", { withTimezone: true }),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("storefront_shopper_sessions_hash_idx").on(t.tokenHash),
    index("storefront_shopper_sessions_shopper_idx").on(t.shopperId),
  ],
);

// ---- rate limiting (fixed window) ------------------------------------------------

export const rateLimitWindows = pgTable(
  "rate_limit_windows",
  {
    /** e.g. "opens:<apiKeyId>" */
    key: text("key").notNull(),
    windowStart: timestamp("window_start", { withTimezone: true }).notNull(),
    count: integer("count").notNull().default(0),
  },
  (t) => [uniqueIndex("rate_limit_windows_pk").on(t.key, t.windowStart)],
);

// ---- stripe connect ---------------------------------------------------------------
// One connected account per (tenant, mode): the live environment charges through the
// live-mode account, every sandbox shares the tenant's test-mode account. Accounts are
// platform-controlled shells (no Stripe dashboard); direct charges are created on
// them with a 100% application fee so all funds sweep to the platform balance.

/** Writer: platform staff create the account; account.updated and status refreshes update its status. Whole row is safe to return to tenant admins. */
export const stripeAccounts = pgTable(
  "stripe_accounts",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    mode: text("mode", { enum: MODES }).notNull(),
    stripeAccountId: text("stripe_account_id").notNull(),
    /** Pre-fill snapshot from the staff create form (KYC lives in Stripe). */
    businessName: text("business_name").notNull(),
    email: text("email").notNull(),
    country: text("country").notNull().default("US"),
    chargesEnabled: boolean("charges_enabled").notNull().default(false),
    payoutsEnabled: boolean("payouts_enabled").notNull().default(false),
    detailsSubmitted: boolean("details_submitted").notNull().default(false),
    /** Mirrored by syncStripeAccountStatus; drives the dashboard status card. */
    requirements: jsonb("requirements")
      .$type<{ currentlyDue: string[]; pastDue: string[]; disabledReason: string | null }>()
      .notNull()
      .default({ currentlyDue: [], pastDue: [], disabledReason: null }),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("stripe_accounts_tenant_mode_idx").on(t.tenantId, t.mode),
    // Webhook tenant resolution: event.account → tenant.
    uniqueIndex("stripe_accounts_stripe_id_idx").on(t.stripeAccountId),
  ],
);

/**
 * Shopper ↔ connected-account Customer mapping. Stripe Customers live per
 * connected account, and a tenant's sandboxes share one test-mode account, so the
 * dedupe axis is the account — not the environment.
 *
 * Writer: the hosted storefront checkout flow. Whole row is safe to return to the owning tenant.
 */
export const stripeCustomers = pgTable(
  "stripe_customers",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    shopperId: text("shopper_id")
      .notNull()
      .references(() => storefrontShoppers.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    stripeAccountId: text("stripe_account_id").notNull(),
    stripeCustomerId: text("stripe_customer_id").notNull(),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("stripe_customers_shopper_account_idx").on(t.shopperId, t.stripeAccountId),
    index("stripe_customers_customer_idx").on(t.stripeCustomerId),
  ],
);

// ---- balance packs + purchases ----------------------------------------------------

/** Writer: tenant editor (balancePacks.create/update). Whole row is safe to return to tenant members. */
export const balancePacks = pgTable(
  "balance_packs",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    /** What the shopper pays, in USD cents (real money). */
    priceCents: integer("price_cents").notNull(),
    /** What lands in the balance, in US cents; more than priceCents for a bonus pack. */
    creditedCents: integer("credited_cents").notNull(),
    /** Packs are never deleted (balance_purchases references them); inactive = archived. */
    active: boolean("active").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("balance_packs_env_idx").on(t.environmentId, t.sortOrder),
    check("balance_packs_price_positive", sql`${t.priceCents} > 0`),
    check("balance_packs_credited_positive", sql`${t.creditedCents} > 0`),
  ],
);

export const BALANCE_PURCHASE_STATUSES = [
  "pending",
  "completed",
  "failed",
  "refunded",
  "disputed",
] as const;
export type BalancePurchaseStatus = (typeof BALANCE_PURCHASE_STATUSES)[number];

/**
 * The fiat side of a balance purchase: one row per Checkout Session. The matching
 * balance_ledger credit row's ref points here; refund/dispute webhooks only flip
 * status (no automatic clawback — support uses ledger adjustments).
 *
 * Writer: the checkout flow and the Stripe webhooks. Whole row is safe to return to tenant admins.
 */
export const balancePurchases = pgTable(
  "balance_purchases",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    shopperId: text("shopper_id")
      .notNull()
      .references(() => storefrontShoppers.id),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id),
    packId: text("pack_id")
      .notNull()
      .references(() => balancePacks.id),
    stripeAccountId: text("stripe_account_id").notNull(),
    checkoutSessionId: text("checkout_session_id").notNull(),
    paymentIntentId: text("payment_intent_id"),
    amountCents: integer("amount_cents").notNull(),
    currency: text("currency").notNull().default("usd"),
    creditedCents: integer("credited_cents").notNull(),
    status: text("status", { enum: BALANCE_PURCHASE_STATUSES }).notNull().default("pending"),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("balance_purchases_session_idx").on(t.checkoutSessionId),
    index("balance_purchases_env_created_idx").on(t.environmentId, t.createdAt),
    // The revenue reports bucket by completed_at (stats.timeseries, payments.revenue).
    index("balance_purchases_env_completed_idx").on(t.environmentId, t.completedAt),
    index("balance_purchases_end_user_idx").on(t.endUserId, t.createdAt),
    index("balance_purchases_payment_intent_idx").on(t.paymentIntentId),
  ],
);

export const STRIPE_DISPUTE_STATUSES = [
  "warning_needs_response",
  "warning_under_review",
  "warning_closed",
  "needs_response",
  "under_review",
  "prevented",
  "won",
  "lost",
] as const;
export type StripeDisputeStatus = (typeof STRIPE_DISPUTE_STATUSES)[number];

/**
 * Local mirror of a Stripe dispute, one row per dispute, kept current by the
 * charge.dispute.* webhooks. Disputes are always against a balance purchase (every
 * charge comes from the balance checkout flow), and with direct charges they debit
 * the connected account — this table is the dashboard's read model; evidence
 * and responses live in Stripe.
 *
 * Writer: the Stripe charge.dispute.* webhooks. Whole row is safe to return to tenant admins.
 */
export const stripeDisputes = pgTable(
  "stripe_disputes",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    purchaseId: text("purchase_id")
      .notNull()
      .references(() => balancePurchases.id, { onDelete: "cascade" }),
    stripeAccountId: text("stripe_account_id").notNull(),
    stripeDisputeId: text("stripe_dispute_id").notNull(),
    chargeId: text("charge_id").notNull(),
    paymentIntentId: text("payment_intent_id"),
    amountCents: integer("amount_cents").notNull(),
    currency: text("currency").notNull().default("usd"),
    reason: text("reason").notNull(),
    status: text("status", { enum: STRIPE_DISPUTE_STATUSES }).notNull(),
    /** Stripe's evidence-submission deadline ("respond by"). */
    evidenceDueBy: timestamp("evidence_due_by", { withTimezone: true }),
    /** When Stripe opened the dispute (the dispute object's `created`). */
    disputedAt: timestamp("disputed_at", { withTimezone: true }).notNull(),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Webhook idempotency anchor: created/updated/closed all upsert on this.
    uniqueIndex("stripe_disputes_stripe_id_idx").on(t.stripeDisputeId),
    index("stripe_disputes_env_created_idx").on(t.environmentId, t.createdAt),
  ],
);

// ---- fraud: assessments + payment-method facts ---------------------------------------

/**
 * One row per SEON screening (or per skipped screening while SEON was unreachable) of a
 * sign-up, checkout or withdrawal. `ref_type`/`ref_id` name the thing screened so a replayed
 * request reuses the verdict instead of billing SEON twice.
 *
 * Writer: the public /v1 register, checkout and fulfillment-order flows. Never reaches a /v1
 * response; the dashboard returns whole rows to tenant members. Deliberately holds no raw IP,
 * email or device session — `signals` is the reduced subset the dashboard shows.
 */
export const fraudAssessments = pgTable(
  "fraud_assessments",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    action: text("action", { enum: FRAUD_ACTIONS }).notNull(),
    state: text("state", { enum: FRAUD_STATES }).notNull(),
    source: text("source", { enum: FRAUD_SOURCES }).notNull(),
    fraudScore: real("fraud_score"),
    /** SEON's transaction id, for looking the call up in SEON's own console. */
    seonId: text("seon_id"),
    appliedRules: jsonb("applied_rules").$type<FraudAppliedRule[]>().notNull().default([]),
    signals: jsonb("signals").$type<FraudSignals>().notNull().default({}),
    /** Why SEON did not answer (`timeout`, `unreachable`, `http_502`, `seon_error`); null when it did. */
    error: text("error"),
    refType: text("ref_type", {
      enum: ["balance_purchase", "inventory_item", "shopper", "end_user"],
    }),
    refId: text("ref_id"),
    ...timestamps,
  },
  (t) => [
    index("fraud_assessments_end_user_idx").on(t.endUserId, t.createdAt),
    index("fraud_assessments_env_idx").on(t.environmentId, t.createdAt),
    index("fraud_assessments_ref_idx").on(t.refType, t.refId),
  ],
);

/**
 * Every distinct (card fingerprint, billing postal code) an end user has paid with, from the
 * Stripe charge. A row whose card *and* postal code were both new for the user is a high-risk
 * payment-method switch, which resets the trust-tier clock (boxed parity: `lastHighRiskSwitchAt`).
 * Fingerprints are scoped to the connected account, one per tenant and mode, so they compare
 * correctly within a tenant.
 *
 * Writer: the Stripe charge.succeeded webhook. card_fingerprint and billing_postal_code must never
 * reach a tenant-facing response; the dashboard shows only `high_risk_switch` and its dates.
 */
export const endUserPaymentMethods = pgTable(
  "end_user_payment_methods",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    stripeAccountId: text("stripe_account_id").notNull(),
    cardFingerprint: text("card_fingerprint").notNull(),
    /** "" when Stripe reported no postal code, so the unique index still holds. */
    billingPostalCode: text("billing_postal_code").notNull().default(""),
    /** Both the fingerprint and the postal code were new for this end user. */
    highRiskSwitch: boolean("high_risk_switch").notNull().default(false),
    firstChargeId: text("first_charge_id").notNull(),
    firstSeenAt: timestamp("first_seen_at", { withTimezone: true }).notNull(),
    lastSeenAt: timestamp("last_seen_at", { withTimezone: true }).notNull(),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("end_user_payment_methods_card_idx").on(
      t.endUserId,
      t.cardFingerprint,
      t.billingPostalCode,
    ),
    index("end_user_payment_methods_end_user_idx").on(t.endUserId, t.firstSeenAt),
  ],
);

// ---- identity verification (KYC) -------------------------------------------------------

/**
 * One row per Veriff session an end user started. Its id is the `vendorData` Veriff echoes
 * back in every webhook, so a webhook resolves to tenant, environment and end user by one
 * lookup and no composite string is ever parsed. boxed keeps Veriff's whole decision payload;
 * this keeps only what the policy and the duplicate check need — no name, date of birth or
 * document number.
 *
 * Writer: the /v1 KYC session routes (create) and the Veriff webhooks (everything after).
 * `session_url`, `identity_hash`, `document_country` and `age_at_decision` must never reach a
 * tenant-facing response; the dashboard returns the projection in `getKycSummary`.
 */
export const kycVerifications = pgTable(
  "kyc_verifications",
  {
    id: text("id").primaryKey(), // ulid, doubles as Veriff vendorData
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    provider: text("provider", { enum: KYC_PROVIDERS }).notNull().default("veriff"),
    /** Veriff's `verification.id`; a webhook naming another session for this row is ignored. */
    providerSessionId: text("provider_session_id").notNull(),
    /** The hosted flow URL; reused while the session is in flight. Never reaches a response. */
    sessionUrl: text("session_url").notNull(),
    status: text("status", { enum: KYC_STATUSES }).notNull().default("created"),
    /** Veriff's numeric decision code (9001 approved, 9102 declined, …). */
    providerCode: integer("provider_code"),
    providerReason: text("provider_reason"),
    /** ISO 3166-1 alpha-2 of the document, for the restricted-country policy. */
    documentCountry: text("document_country"),
    /** Whole years at decision time; ≥ the minimum age once is ≥ it forever, so no DOB is kept. */
    ageAtDecision: integer("age_at_decision"),
    /** sha256 of the normalized `country#first last#dob` (boxed's duplicate-person key). */
    identityHash: text("identity_hash"),
    /** Another end user in the environment was approved with the same identity. */
    isDuplicate: boolean("is_duplicate").notNull().default(false),
    /** Why an approved decision still does not verify the user; null when it does or is not final. */
    policyIssue: text("policy_issue", { enum: KYC_POLICY_ISSUES }),
    decidedAt: timestamp("decided_at", { withTimezone: true }),
    ...timestamps,
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("kyc_verifications_provider_session_idx").on(t.provider, t.providerSessionId),
    index("kyc_verifications_end_user_idx").on(t.endUserId, t.createdAt),
    index("kyc_verifications_identity_idx").on(t.environmentId, t.identityHash),
  ],
);

// ---- platform switches (platform-wide enable/disable) -----------------------------

/**
 * The actions a platform admin can disable during an incident. Members are passed into the `key`
 * column below, so a stray literal fails to compile and there is no second copy of the set.
 * Adding one means adding its gate: every key is read by exactly one `assertSwitchOn` call.
 */
export const PLATFORM_SWITCH_KEYS = [
  "opens",
  "claims",
  "battles",
  "topups",
  "orders",
  "order_cancels",
  "procurement_buying",
  "quickbooks",
  "chat",
  "forge",
  "stockx_scraping",
] as const;

/**
 * Platform-wide switches, one row per switch. Writer: platform staff (`platform.switches.set`).
 * Whole row is safe to return to any dashboard member: it names an action and whether the
 * platform is running it, nothing tenant-specific.
 *
 * `enabled` is TRUE = running — the inverse of boxed's kill switches, where true meant killed —
 * so a missing row, a fresh database and an unreachable database all permit traffic. Nothing
 * creates rows up front: `listSwitches` reports an absent key as enabled and
 * `platform.switches.set` upserts the one key it writes.
 *
 * Tenant-level pauses are elsewhere and stay there: `tenant_settings.opens_paused`,
 * `storefront_configs.enabled` and the per-environment `features.battles`.
 */
export const platformSwitches = pgTable("platform_switches", {
  key: text("key", { enum: PLATFORM_SWITCH_KEYS }).primaryKey(),
  /** TRUE = the platform runs this action; FALSE = disabled for every tenant. */
  enabled: boolean("enabled").notNull().default(true),
  /**
   * The Better Auth user id of the platform admin who last flipped it; NULL until someone
   * does. No FK, for the reason `audit_logs.actor_id` carries none: this is a record of who
   * acted, and it should outlive the account.
   */
  updatedBy: text("updated_by"),
  ...timestamps,
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
